/**
 * 安全的字符串处理工具集
 * 处理UTF-16编码中的代理对问题，支持表情符号、组合字符等
 */
export class SafeString {
  /**
   * 字符串转换为字符数组（正确处理代理对）
   * @param {string} str - 输入字符串
   * @returns {string[]} 字符数组
   */
  static toArray(str: string) {
    return Array.from(str)
  }

  /**
   * 安全分割字符串（替代 split('')）
   * @param {string} str - 输入字符串
   * @param {string|RegExp} separator - 分隔符
   * @param {number} limit - 限制结果数量
   * @returns {string[]} 分割后的数组
   */
  static split(str: string, separator: string | RegExp = '', limit: number) {
    if (separator === '') {
      return this.toArray(str).slice(0, limit)
    }

    if (separator instanceof RegExp) {
      if (!separator.unicode) {
        separator = new RegExp(separator.source, separator.flags + 'u')
      }
      return str.split(separator, limit)
    }

    return str.split(separator, limit)
  }

  /**
   * 安全获取子字符串
   * @param {string} str - 输入字符串
   * @param {number} start - 开始位置
   * @param {number} end - 结束位置
   * @returns {string} 子字符串
   */
  static substring(str: string, start: number, end: number) {
    const chars = this.toArray(str)
    return chars.slice(start, end).join('')
  }

  /**
   * 安全获取字符长度
   * @param {string} str - 输入字符串
   * @returns {number} 字符数量（不是码元数量）
   */
  static getLength(str: string) {
    return this.toArray(str).length
  }

  /**
   * 安全反转字符串
   * @param {string} str - 输入字符串
   * @returns {string} 反转后的字符串
   */
  static reverse(str: string) {
    return this.toArray(str).reverse().join('')
  }

  /**
   * 安全截断字符串
   * @param {string} str - 输入字符串
   * @param {number} maxLength - 最大长度
   * @param {string} suffix - 后缀，默认为"..."
   * @returns {string} 截断后的字符串
   */
  static truncate(str: string, maxLength: number, suffix = '...') {
    const chars = this.toArray(str)
    if (chars.length <= maxLength) return str
    return chars.slice(0, maxLength).join('') + suffix
  }

  /**
   * 查找字符索引
   * @param {string} str - 输入字符串
   * @param {string} searchChar - 要查找的字符
   * @param {number} fromIndex - 起始位置
   * @returns {number} 索引，如果未找到返回-1
   */
  static indexOfChar(str: string, searchChar: string, fromIndex = 0) {
    const chars = this.toArray(str)
    for (let i = fromIndex; i < chars.length; i++) {
      if (chars[i] === searchChar) return i
    }
    return -1
  }

  /**
   * 验证UTF-16字符串是否有效
   * @param {string} str - 输入字符串
   * @returns {boolean} 是否有效
   */
  static isValidUTF16(str: string) {
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i)

      // 检查是否是高位代理
      if (code >= 0xd800 && code <= 0xdbff) {
        // 确保后面有字符且是低位代理
        if (i + 1 >= str.length) return false
        const next = str.charCodeAt(i + 1)
        if (next < 0xdc00 || next > 0xdfff) return false
        i++ // 跳过低位代理
      }
      // 检查是否是孤立的低位代理
      else if (code >= 0xdc00 && code <= 0xdfff) {
        return false
      }
    }
    return true
  }

  /**
   * 修复不完整的UTF-16字符串
   * @param {string} str - 输入字符串
   * @param {string} replacement - 替换字符，默认为"�"
   * @returns {string} 修复后的字符串
   */
  static fixUTF16(str: string, replacement = '�') {
    const result = []

    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i)

      if (code >= 0xd800 && code <= 0xdbff) {
        // 高位代理
        if (i + 1 < str.length) {
          const next = str.charCodeAt(i + 1)
          if (next >= 0xdc00 && next <= 0xdfff) {
            // 完整的代理对
            result.push(str[i] + str[i + 1])
            i++
          } else {
            // 不完整，用替换字符
            result.push(replacement)
          }
        } else {
          result.push(replacement)
        }
      } else if (code >= 0xdc00 && code <= 0xdfff) {
        // 孤立的低位代理
        result.push(replacement)
      } else {
        result.push(str[i])
      }
    }

    return result.join('')
  }

  /**
   * 按单词分割（支持表情符号）
   * @param {string} str - 输入字符串
   * @returns {string[]} 单词数组
   */
  static splitWords(str: string) {
    // 匹配字母、表情符号、汉字等
    return str.match(/[\p{L}\p{Emoji}\p{N}]+|[^\s]/gu) || []
  }

  /**
   * 按行分割，支持各种换行符
   * @param {string} str - 输入字符串
   * @returns {string[]} 行数组
   */
  static splitLines(str: string) {
    return str.split(/\r\n|\n|\r/)
  }

  /**
   * 安全迭代字符串
   * @param {string} str - 输入字符串
   * @yields {string} 每个字符
   */
  static *iterate(str: string) {
    let i = 0
    while (i < str.length) {
      const code = str.charCodeAt(i)

      if (code >= 0xd800 && code <= 0xdbff && i + 1 < str.length) {
        const next = str.charCodeAt(i + 1)
        if (next >= 0xdc00 && next <= 0xdfff) {
          yield str[i] + str[i + 1]
          i += 2
          continue
        }
      }

      yield str[i]
      i++
    }
  }

  /**
   * 获取字符的Unicode码点
   * @param {string} char - 单个字符
   * @returns {number} Unicode码点
   */
  static codePointAt(str: string, position: number) {
    const chars = this.toArray(str)
    if (position < 0 || position >= chars.length) return undefined

    const char = chars[position]
    if (char.length === 1) {
      return char.charCodeAt(0)
    } else {
      return char.codePointAt(0)
    }
  }

  /**
   * 从码点创建字符
   * @param {...number} codePoints - Unicode码点
   * @returns {string} 字符
   */
  static fromCodePoint(...codePoints: number[]) {
    return String.fromCodePoint(...codePoints)
  }

  /**
   * 统计字符信息
   * @param {string} str - 输入字符串
   * @returns {Object} 统计信息
   */
  static analyze(str: string) {
    const chars = this.toArray(str)
    const codeUnits = str.length

    let surrogatePairs = 0
    let emojis = 0
    let ascii = 0
    let nonAscii = 0

    for (const char of chars) {
      if (char.length === 2) surrogatePairs++
      if (/\p{Emoji}/u.test(char)) emojis++
      if (/^[\x00-\x7F]$/.test(char)) ascii++
      else nonAscii++
    }

    return {
      characters: chars.length,
      codeUnits,
      surrogatePairs,
      emojis,
      ascii,
      nonAscii,
      bytes: new Blob([str]).size,
    }
  }

  /**
   * 比较两个字符串（考虑Unicode规范化）
   * @param {string} a - 第一个字符串
   * @param {string} b - 第二个字符串
   * @param {boolean} normalize - 是否进行Unicode规范化
   * @returns {boolean} 是否相等
   */
  static equals(a: string, b: string, normalize = true) {
    if (normalize) {
      return a.normalize() === b.normalize()
    }
    return a === b
  }

  /**
   * 移除无效字符
   * @param {string} str - 输入字符串
   * @returns {string} 清理后的字符串
   */
  static clean(str: string) {
    return this.fixUTF16(str, '')
  }
}
